/**
 * TYPE A FEW LETTERS, AND BE SHOWN THE THING YOU MEANT.
 *
 * The owner, after being handed a grid of every item on the menu:
 *
 * > "Stop trying to do a list.. You need to think about autosuggestions only... Create flows that
 * >  update. Are we searching for a partner? Are we editing something about an existing partner?
 * >  Are we adding a new partner?"
 *
 * The portal had no type-ahead at all. Its only pickers are `EntityField`'s, backed by
 * `hooks/useEntityIndex.ts`, which fetches THE WHOLE COLLECTION with one `rawApi('GET', listPath)`
 * and filters it in the browser — the preloaded grid he rejected, and the answer this platform
 * refuses to the question behind every list surface: what is fetched when there are a million rows?
 *
 * So: nothing is fetched until somebody types, the SERVER does the matching, and what comes back is
 * a capped page that says how much it left out. The component cannot be made to render a collection
 * — there is no code path here that asks for one.
 *
 * ## Why the resource is declared rather than passed
 *
 * `SEARCHABLE` below is the same idea as `lib/entities.ts`: one entry per collection, so the second
 * screen that needs to pick a menu size reuses this instead of writing a second picker with its own
 * debounce, its own cap and its own idea of when to start looking. The difference is the endpoint's
 * contract — an entry here names a door that SEARCHES, and there is exactly one of those today.
 */
import { Combobox, Group, Loader, Text, TextInput, useCombobox } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';

/**
 * What a searching endpoint answers. The shape is `domains/reference/pos-routes.ts`'s, which is the
 * same problem already solved for the register's people picker: a page, a total, and whether the total
 * is the truth or a floor.
 */
export interface SearchPage<T> {
  hits: T[];
  total: number;
  /** False when more matched than the server was willing to count — `total` is then a floor. */
  totalIsExact: boolean;
}

export interface SearchResource<T> {
  /** A door that takes `q` and `limit` and answers a capped page. Never a list endpoint. */
  path: string;
  /** Singular noun, for the line that says nothing matched. */
  noun: string;
  /**
   * How much has to be typed before anything is fetched or drawn. Two, because one letter matches
   * most of a menu and asking the server about it is a request whose answer is never used.
   */
  minChars: number;
  value: (hit: T) => string;
  label: (hit: T) => string;
  /** The second line on an option — what tells two similarly named things apart. */
  hint?: (hit: T) => string | undefined;
  /** The right-hand side of an option, where a price or a count belongs. */
  meta?: (hit: T) => ReactNode;
}

/** One size on the menu, as the search door answers it. */
export interface VariantHit {
  variantId: string;
  itemName: string;
  sizeName: string;
  categoryName: string | null;
  /** What the published card charges for this size, or null where it prices it nowhere. */
  everydayUsdMinor: number | null;
}

export const SEARCHABLE = {
  /**
   * Sizes on the menu. Deliberately the SIZE and not the dish: a product has up to fourteen of them
   * and the parent is never priced, so a picker that returned dishes would hand back something
   * nothing can be charged for.
   */
  variant: {
    path: '/admin/v1/catalog/variant-search',
    noun: 'item',
    minChars: 2,
    value: (hit) => hit.variantId,
    label: (hit) => `${hit.itemName} — ${hit.sizeName}`,
    hint: (hit) => hit.categoryName ?? undefined,
    /**
     * THE MENU PRICE ON THE OPTION. "$6 against $9" only works if $9 is visible while choosing.
     * A picker that names sizes without prices is how you pick the wrong pour of the same beer.
     */
    meta: (hit) =>
      hit.everydayUsdMinor == null ? undefined : (
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          ${(hit.everydayUsdMinor / 100).toFixed(2)}
        </Text>
      ),
  } satisfies SearchResource<VariantHit>,
};

export interface SearchPickerProps<T> {
  resource: SearchResource<T>;
  label?: string;
  description?: ReactNode;
  placeholder?: string;
  /** Appended to every request — the location, a status filter, whatever the door takes. */
  params?: Record<string, string | undefined>;
  onPick: (hit: T) => void;
  disabled?: boolean;
  /** For the tests and for a label's `htmlFor`. */
  id?: string;
}

export function SearchPicker<T>(props: SearchPickerProps<T>) {
  const { resource } = props;
  const combobox = useCombobox();
  const [typed, setTyped] = useState('');
  /*
   * A REQUEST PER KEYSTROKE IS A REQUEST PER KEYSTROKE. 200ms is long enough that "kozel" is one
   * search rather than five, and short enough that nobody waits for it.
   */
  const [q] = useDebouncedValue(typed, 200);
  const ready = q.trim().length >= resource.minChars;

  const search = useQuery({
    queryKey: listKey(resource.path, { q, ...props.params }),
    queryFn: () => {
      const query = new URLSearchParams({ q, limit: '10' });
      for (const [key, value] of Object.entries(props.params ?? {})) {
        if (value) query.set(key, value);
      }
      return rawApi<SearchPage<T>>('GET', `${resource.path}?${query.toString()}`);
    },
    // NOTHING IS ASKED UNTIL SOMETHING IS TYPED. This is the whole difference from `useEntityIndex`.
    enabled: ready,
    /*
     * The previous page stays on screen while the next one is in flight, so the list does not blink
     * empty between "koz" and "koze" — an empty frame mid-word reads as "no such item" and people
     * stop typing.
     */
    placeholderData: keepPreviousData,
  });

  const page = ready ? search.data : undefined;
  const hits = page?.hits ?? [];
  /** More matched than came back — said with a number, because "keep typing" alone is a shrug. */
  const withheld = page ? page.total - hits.length : 0;

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(value) => {
        const hit = hits.find((h) => resource.value(h) === value);
        if (!hit) return;
        props.onPick(hit);
        setTyped('');
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <TextInput
          id={props.id}
          label={props.label}
          description={props.description}
          placeholder={props.placeholder ?? 'Start typing'}
          value={typed}
          disabled={props.disabled}
          onChange={(event) => {
            setTyped(event.currentTarget.value);
            combobox.openDropdown();
            combobox.resetSelectedOption();
          }}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={search.isFetching ? <Loader size="xs" /> : null}
        />
      </Combobox.Target>

      {/*
        THE DROPDOWN DOES NOT EXIST UNTIL YOU HAVE TYPED ENOUGH. Not "is empty" — is not rendered.
        A picker that shows a frame the moment it is focused is a list, and it is the thing he
        rejected, whatever is or is not inside the frame.
      */}
      {ready && (
        <Combobox.Dropdown>
          <Combobox.Options>
            {hits.map((hit) => {
              const value = resource.value(hit);
              const hint = resource.hint?.(hit);
              return (
                <Combobox.Option value={value} key={value}>
                  <Group justify="space-between" wrap="nowrap" gap="sm">
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm">{resource.label(hit)}</Text>
                      {hint && (
                        <Text size="xs" c="dimmed">
                          {hint}
                        </Text>
                      )}
                    </div>
                    {resource.meta?.(hit)}
                  </Group>
                </Combobox.Option>
              );
            })}

            {hits.length === 0 && !search.isFetching && (
              <Combobox.Empty>No {resource.noun} matches that.</Combobox.Empty>
            )}

            {withheld > 0 && (
              <Combobox.Footer>
                <Text size="xs" c="dimmed">
                  {page?.totalIsExact
                    ? `${withheld} more match — keep typing.`
                    : 'Many more match — keep typing.'}
                </Text>
              </Combobox.Footer>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      )}
    </Combobox>
  );
}
